import {
  ComponentType,
  FC,
  LazyExoticComponent,
  PropsWithChildren,
  Suspense,
  lazy,
} from 'react';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';

import AppLayout from '@/components/layouts/app-layout';
import { LoadingFullScreen } from '@/components/helpers/loading';

type RouteParams = Record<string, string | undefined>;
type StaticRouteComponent = LazyExoticComponent<ComponentType<object>>;
type ParamsRouteComponent = LazyExoticComponent<
  ComponentType<{ params: RouteParams }>
>;
type LayoutComponent = LazyExoticComponent<ComponentType<PropsWithChildren>>;

const lazyPage = (loader: () => Promise<object>): StaticRouteComponent =>
  lazy(loader as () => Promise<{ default: ComponentType<object> }>);

const lazyParamsPage = (loader: () => Promise<object>): ParamsRouteComponent =>
  lazy(
    loader as () => Promise<{ default: ComponentType<{ params: RouteParams }> }>
  );

const lazyLayout = (loader: () => Promise<object>): LayoutComponent =>
  lazy(loader as () => Promise<{ default: ComponentType<PropsWithChildren> }>);

const HomePage = lazyPage(() => import('@/app/(application)/(webshop)/page'));
const InventoryPage = lazyPage(
  () => import('@/app/(application)/(webshop)/inventory/page')
);
const OrdersPage = lazyPage(
  () => import('@/app/(application)/(webshop)/orders/page')
);
const OrderDetailPage = lazyParamsPage(
  () => import('@/app/(application)/(webshop)/orders/[orderId]/page')
);
const OrderActivityLogPage = lazyParamsPage(
  () =>
    import('@/app/(application)/(webshop)/orders/[orderId]/activity-log/page')
);
const FaqsPage = lazyPage(
  () => import('@/app/(application)/(webshop)/faqs/page')
);
const FaqTypePage = lazyParamsPage(
  () => import('@/app/(application)/(webshop)/faqs/[type]/page')
);
const WebshopLayout = lazyLayout(
  () => import('@/app/(application)/(webshop)/layout')
);
const ShopLayout = lazyLayout(
  () => import('@/app/(application)/(webshop)/shop/layout')
);
const ShopPage = lazyPage(
  () => import('@/app/(application)/(webshop)/shop/page')
);
const ShopLocationPage = lazyParamsPage(
  () => import('@/app/(application)/(webshop)/shop/[locationSlug]/page')
);
const ShopItemPage = lazyParamsPage(
  () =>
    import(
      '@/app/(application)/(webshop)/shop/[locationSlug]/items/[item]/page'
    )
);
const ShopCheckoutPage = lazyPage(
  () => import('@/app/(application)/(webshop)/shop/checkout/page')
);
const ShopCheckoutConfirmationPage = lazyPage(
  () => import('@/app/(application)/(webshop)/shop/checkout/confirmation/page')
);
const WardrobeLayout = lazyLayout(
  () => import('@/app/(application)/(webshop)/wardrobe/layout')
);
const WardrobePage = lazyPage(
  () => import('@/app/(application)/(webshop)/wardrobe/page')
);
const WardrobeLocationPage = lazyParamsPage(
  () => import('@/app/(application)/(webshop)/wardrobe/[locationSlug]/page')
);
const WardrobeItemPage = lazyParamsPage(
  () =>
    import(
      '@/app/(application)/(webshop)/wardrobe/[locationSlug]/items/[item]/page'
    )
);
const WardrobeCheckoutPage = lazyPage(
  () => import('@/app/(application)/(webshop)/wardrobe/checkout/page')
);
const WardrobeCheckoutConfirmationPage = lazyPage(
  () =>
    import('@/app/(application)/(webshop)/wardrobe/checkout/confirmation/page')
);
const AdminLayout = lazyLayout(
  () => import('@/app/(application)/(admin)/layout')
);
const AdminIndexPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/page')
);
const AdminActivityLogPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/activity-log/page')
);
const AdminInvitationsPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/invitations/page')
);
const AdminItemsLayout = lazyLayout(
  () => import('@/app/(application)/(admin)/admin/items/layout')
);
const AdminItemsPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/items/page')
);
const AdminItemsGlobalPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/items/global/page')
);
const AdminItemDetailPage = lazyParamsPage(
  () => import('@/app/(application)/(admin)/admin/items/[itemId]/page')
);
const AdminItemActivityLogPage = lazyParamsPage(
  () =>
    import('@/app/(application)/(admin)/admin/items/[itemId]/activity-log/page')
);
const AdminItemUsersPage = lazyParamsPage(
  () => import('@/app/(application)/(admin)/admin/items/[itemId]/users/page')
);
const AdminOrdersLayout = lazyLayout(
  () => import('@/app/(application)/(admin)/admin/orders/layout')
);
const AdminOrdersPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/orders/page')
);
const AdminOrderDetailPage = lazyParamsPage(
  () => import('@/app/(application)/(admin)/admin/orders/[orderId]/page')
);
const AdminOrderActivityLogPage = lazyParamsPage(
  () =>
    import(
      '@/app/(application)/(admin)/admin/orders/[orderId]/activity-log/page'
    )
);
const AdminSettingsPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/settings/page')
);
const AdminUsersLayout = lazyLayout(
  () => import('@/app/(application)/(admin)/admin/users/layout')
);
const AdminUsersPage = lazyPage(
  () => import('@/app/(application)/(admin)/admin/users/page')
);
const AdminUserDetailPage = lazyParamsPage(
  () => import('@/app/(application)/(admin)/admin/users/[userEmail]/page')
);
const AdminUserActivityLogPage = lazyParamsPage(
  () =>
    import(
      '@/app/(application)/(admin)/admin/users/[userEmail]/activity-log/page'
    )
);
const AdminUserInventoryPage = lazyParamsPage(
  () =>
    import('@/app/(application)/(admin)/admin/users/[userEmail]/inventory/page')
);
const AdminWarehousesLayout = lazyLayout(
  () => import('@/app/(application)/(admin)/admin/warehouses/layout')
);
const AdminWarehouseDetailPage = lazyParamsPage(
  () =>
    import('@/app/(application)/(admin)/admin/warehouses/[warehouseId]/page')
);
const SuperadminLayout = lazyLayout(
  () => import('@/app/(application)/(superadmin)/layout')
);
const SuperadminIndexPage = lazyPage(
  () => import('@/app/(application)/(superadmin)/superadmin/page')
);
const SuperadminLocationsPage = lazyPage(
  () => import('@/app/(application)/(superadmin)/superadmin/locations/page')
);

const RouteFallback = () => <LoadingFullScreen />;

const StaticRouteElement: FC<{ Component: StaticRouteComponent }> = ({
  Component,
}) => {
  const RouteComponent = Component as ComponentType<object>;

  return (
    <Suspense fallback={<RouteFallback />}>
      <RouteComponent />
    </Suspense>
  );
};

const ParamsRouteElement: FC<{ Component: ParamsRouteComponent }> = ({
  Component,
}) => {
  const params = useParams();
  const RouteComponent = Component as ComponentType<{ params: RouteParams }>;

  return (
    <Suspense fallback={<RouteFallback />}>
      <RouteComponent params={params} />
    </Suspense>
  );
};

const LayoutRouteElement: FC<{ Component: LayoutComponent }> = ({
  Component,
}) => (
  <Suspense fallback={<RouteFallback />}>
    <Component>
      <Outlet />
    </Component>
  </Suspense>
);

const AppRouter: FC = () => (
  <Routes>
    <Route element={<AppLayout />}>
      <Route element={<LayoutRouteElement Component={WebshopLayout} />}>
        <Route index element={<StaticRouteElement Component={HomePage} />} />
        <Route
          path="inventory"
          element={<StaticRouteElement Component={InventoryPage} />}
        />
        <Route
          path="orders"
          element={<StaticRouteElement Component={OrdersPage} />}
        />
        <Route
          path="orders/:orderId"
          element={<ParamsRouteElement Component={OrderDetailPage} />}
        />
        <Route
          path="orders/:orderId/activity-log"
          element={<ParamsRouteElement Component={OrderActivityLogPage} />}
        />
        <Route
          path="faqs"
          element={<StaticRouteElement Component={FaqsPage} />}
        />
        <Route
          path="faqs/:type"
          element={<ParamsRouteElement Component={FaqTypePage} />}
        />
        <Route
          path="shop"
          element={<LayoutRouteElement Component={ShopLayout} />}
        >
          <Route index element={<StaticRouteElement Component={ShopPage} />} />
          <Route
            path=":locationSlug"
            element={<ParamsRouteElement Component={ShopLocationPage} />}
          />
          <Route
            path=":locationSlug/items/:item"
            element={<ParamsRouteElement Component={ShopItemPage} />}
          />
          <Route
            path="checkout"
            element={<StaticRouteElement Component={ShopCheckoutPage} />}
          />
          <Route
            path="checkout/confirmation"
            element={
              <StaticRouteElement Component={ShopCheckoutConfirmationPage} />
            }
          />
        </Route>
        <Route
          path="wardrobe"
          element={<LayoutRouteElement Component={WardrobeLayout} />}
        >
          <Route
            index
            element={<StaticRouteElement Component={WardrobePage} />}
          />
          <Route
            path=":locationSlug"
            element={<ParamsRouteElement Component={WardrobeLocationPage} />}
          />
          <Route
            path=":locationSlug/items/:item"
            element={<ParamsRouteElement Component={WardrobeItemPage} />}
          />
          <Route
            path="checkout"
            element={<StaticRouteElement Component={WardrobeCheckoutPage} />}
          />
          <Route
            path="checkout/confirmation"
            element={
              <StaticRouteElement
                Component={WardrobeCheckoutConfirmationPage}
              />
            }
          />
        </Route>
      </Route>

      <Route
        path="admin"
        element={<LayoutRouteElement Component={AdminLayout} />}
      >
        <Route
          index
          element={<StaticRouteElement Component={AdminIndexPage} />}
        />
        <Route
          path="activity-log"
          element={<StaticRouteElement Component={AdminActivityLogPage} />}
        />
        <Route
          path="invitations"
          element={<StaticRouteElement Component={AdminInvitationsPage} />}
        />
        <Route
          path="items"
          element={<LayoutRouteElement Component={AdminItemsLayout} />}
        >
          <Route
            index
            element={<StaticRouteElement Component={AdminItemsPage} />}
          />
          <Route
            path="global"
            element={<StaticRouteElement Component={AdminItemsGlobalPage} />}
          />
          <Route
            path=":itemId"
            element={<ParamsRouteElement Component={AdminItemDetailPage} />}
          />
          <Route
            path=":itemId/activity-log"
            element={
              <ParamsRouteElement Component={AdminItemActivityLogPage} />
            }
          />
          <Route
            path=":itemId/users"
            element={<ParamsRouteElement Component={AdminItemUsersPage} />}
          />
        </Route>
        <Route
          path="orders"
          element={<LayoutRouteElement Component={AdminOrdersLayout} />}
        >
          <Route
            index
            element={<StaticRouteElement Component={AdminOrdersPage} />}
          />
          <Route
            path=":orderId"
            element={<ParamsRouteElement Component={AdminOrderDetailPage} />}
          />
          <Route
            path=":orderId/activity-log"
            element={
              <ParamsRouteElement Component={AdminOrderActivityLogPage} />
            }
          />
        </Route>
        <Route
          path="settings"
          element={<StaticRouteElement Component={AdminSettingsPage} />}
        />
        <Route
          path="users"
          element={<LayoutRouteElement Component={AdminUsersLayout} />}
        >
          <Route
            index
            element={<StaticRouteElement Component={AdminUsersPage} />}
          />
          <Route
            path=":userEmail"
            element={<ParamsRouteElement Component={AdminUserDetailPage} />}
          />
          <Route
            path=":userEmail/activity-log"
            element={
              <ParamsRouteElement Component={AdminUserActivityLogPage} />
            }
          />
          <Route
            path=":userEmail/inventory"
            element={<ParamsRouteElement Component={AdminUserInventoryPage} />}
          />
        </Route>
        <Route
          path="warehouses"
          element={<LayoutRouteElement Component={AdminWarehousesLayout} />}
        >
          <Route
            path=":warehouseId"
            element={
              <ParamsRouteElement Component={AdminWarehouseDetailPage} />
            }
          />
        </Route>
      </Route>

      <Route
        path="superadmin"
        element={<LayoutRouteElement Component={SuperadminLayout} />}
      >
        <Route
          index
          element={<StaticRouteElement Component={SuperadminIndexPage} />}
        />
        <Route
          path="locations"
          element={<StaticRouteElement Component={SuperadminLocationsPage} />}
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>
);

export default AppRouter;
